import labels from "../../utils/labels";
import KPIScaleWithData from "../UI/kpi/KPIScaleWithData";
import KpiCard from "./KpiCard";
import { ReactComponent as MedalIcon } from "../../assets/icons/medal.svg";
import { useAppSelector } from "../../store/hooks";
import colors from "../../utils/colors";
import { useEffect, useState } from "react";
import { KpiPlan } from "../../types/kpi";
import comparisonUtil from "../../utils/comparisonUtil";
import ComparisonChip from "../UI/chips/ComparisonChip";
import KpiComparisonContinuity from "./KpiComparisonContinuity";
import KpiComparisonPreference from "./KpiComparisonPreference";
import KpiComparisonTimePreference from "./KpiComparisonTimePreference";

export type ScaleColor = {
  backgroundColor: string;
  borderColor: string;
};

const CitizenServiceCard = ({
  kpiList,
  compare,
  compareKpiList,
}: {
  kpiList: KpiPlan;
  compare: boolean;
  compareKpiList: KpiPlan;
}) => {
  const { kpiData } = useAppSelector((state) => state.kpiCollectionSlice);
  const [continuityColor, setContinuityColor] = useState<ScaleColor>();
  const [rulesColor, setRulesColor] = useState<ScaleColor>();
  const [timePreferencesColor, setTimePreferencesColor] =
    useState<ScaleColor>();
  const [employeePreferencesColor, setEmployeePreferencesColor] =
    useState<ScaleColor>();

  // Special considerations for percentage preferences
  const percentCompliedTimePreferences =
    kpiList.totalTimePreferences > 0
      ? kpiList.percentCompliedTimePreferences
      : 100;

  const percentCompliedEmployeePreferences =
    kpiList.totalEmployeePreferences > 0
      ? kpiList.percentCompliedEmployeePreferences
      : 100;

  useEffect(() => {
    if (kpiData) {
      setContinuityColor(
        colors.getContinuityAndPreferenceScaleColor(
          Math.round((kpiList.continuity / 10) * 100),
        ),
      );
      setRulesColor(colors.getRulesScaleColor(kpiList.percentCompliedRules));
      setTimePreferencesColor(
        colors.getContinuityAndPreferenceScaleColor(
          percentCompliedTimePreferences,
        ),
      );
      setEmployeePreferencesColor(
        colors.getContinuityAndPreferenceScaleColor(
          percentCompliedEmployeePreferences,
        ),
      );
    }
  }, [kpiData]);

  return (
    <KpiCard
      title={labels.citizenService}
      icon={<MedalIcon />}
      classes="w-full h-[323px]"
    >
      {kpiData?.pondooDashboard && (
        <div className="flex gap-spacing-xl flex-col">
          {continuityColor && (
            <KPIScaleWithData
              title={labels.affinity}
              value={comparisonUtil.getRoundedValue(kpiList.continuity)}
              percent={Math.round((kpiList.continuity / 10) * 100)}
              color={continuityColor}
            >
              {compare && (
                <KpiComparisonContinuity
                  value={kpiList.continuity}
                  compareValue={compareKpiList.continuity}
                />
              )}
            </KPIScaleWithData>
          )}
          {rulesColor && (
            <KPIScaleWithData
              title={labels.rulesCompliance}
              value={`${kpiList.percentCompliedRules}%`}
              percent={kpiList.percentCompliedRules}
              color={rulesColor}
            >
              {compare && (
                <ComparisonChip
                  positiveIndicator="POSITIVE"
                  value={
                    kpiList.percentCompliedRules -
                    compareKpiList.percentCompliedRules
                  }
                  id={compareKpiList.percentCompliedRules}
                  unit="%"
                />
              )}
            </KPIScaleWithData>
          )}
          {employeePreferencesColor && (
            <KPIScaleWithData
              title={labels.preferencesCompliance}
              value={`${percentCompliedEmployeePreferences}%`}
              percent={percentCompliedEmployeePreferences}
              color={employeePreferencesColor}
            >
              {compare && (
                <KpiComparisonPreference
                  total={kpiList.totalEmployeePreferences}
                  percentComplied={kpiList.percentCompliedEmployeePreferences}
                  compareTotal={compareKpiList.totalEmployeePreferences}
                  comparePercentComplied={
                    compareKpiList.percentCompliedEmployeePreferences
                  }
                />
              )}
            </KPIScaleWithData>
          )}
          {timePreferencesColor && (
            <KPIScaleWithData
              title={labels.preferredTimeCompliance}
              value={`${percentCompliedTimePreferences}%`}
              percent={percentCompliedTimePreferences}
              color={timePreferencesColor}
            >
              {compare && (
                <KpiComparisonTimePreference
                  total={kpiList.totalTimePreferences}
                  percentComplied={kpiList.percentCompliedTimePreferences}
                  compareTotal={compareKpiList.totalTimePreferences}
                  comparePercentComplied={
                    compareKpiList.percentCompliedTimePreferences
                  }
                />
              )}
            </KPIScaleWithData>
          )}
        </div>
      )}
    </KpiCard>
  );
};

export default CitizenServiceCard;
